import { QueryContextFromKeys, QueryParamModel } from 'models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { swapOrderIdsService } from 'services/swapOrderIds.service';
import { REACT_QUERY_OPTIONS } from 'config/reactQueryConfig';

type LoadTasksContext = QueryContextFromKeys<typeof swapOrderIdsKeys>;

export const swapOrderIdsKeys = {
  all: [{ scope: 'swapOrderIds' }] as const,
  lists: () => [{ ...swapOrderIdsKeys.all[0], entity: 'list' }] as const,
  list: (params?: QueryParamModel | null) => [{ ...swapOrderIdsKeys.lists()[0], params }] as const,
  download: (id: string) => [{ ...swapOrderIdsKeys.all[0], entity: 'download', id }] as const,
  active: () => [{ ...swapOrderIdsKeys.all[0], entity: 'active' }] as const,
  status: (id: string) => [{ ...swapOrderIdsKeys.all[0], entity: 'status', id }] as const,
};

const fetchSwapOrderIdsList = async ({ queryKey: [{ params }] }: LoadTasksContext['list']) =>
  swapOrderIdsService.getTasks(params);

export const useSwapOrderIdsList = (params?: QueryParamModel | null) =>
  useQuery({
    queryKey: swapOrderIdsKeys.list(params),
    queryFn: fetchSwapOrderIdsList,
    enabled: !!params?.filter,
  });

export const useSwapOrderIdsUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => swapOrderIdsService.upload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: swapOrderIdsKeys.lists() });
    },
  });
};

export const useRetrySwapOrderIdUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => swapOrderIdsService.retryUpload(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: swapOrderIdsKeys.lists() });
    },
  });
};

export const useDownloadSwapOrderIdResult = () => {
  return useMutation({
    mutationFn: (id: string) => swapOrderIdsService.downloadResult(id),
    meta: {
      [REACT_QUERY_OPTIONS.SKIP_DEFAULT_SUCCESS_CALLBACK]: true,
    },
  });
};

export const useSwapOrderIdActiveTask = () =>
  useQuery({
    queryKey: swapOrderIdsKeys.active(),
    queryFn: async () => swapOrderIdsService.getActiveTasks(),
  });

const JOB_STATUS_REFETCH_INTERVAL = 3000;

export const useJobStatus = (id: string) => {
  return useQuery({
    queryKey: swapOrderIdsKeys.status(id),
    queryFn: async () => swapOrderIdsService.getJobStatus(id),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.status !== 'completed' ? JOB_STATUS_REFETCH_INTERVAL : false,
  });
};
